// Kho hồ sơ 360 (mock, localStorage — ảnh/chữ ký nằm trong IndexedDB): liên kết hồ sơ POS, phân loại tay, ảnh liệu trình, văn bản đã ký
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  DEFAULT_C360_CONFIG, SIGN_KIND, type ClassLog, type ClassOverride, type Customer360Config, type DocSection, type AuditLog, type DemoMeta, type SignDoc, type SignKind, type TreatmentPhoto, type VisitRecord,
} from './data';
import { SEGMENT } from './logic';
import { delMedia, digest, putMedia } from './mediaDb';

export const C360_STORAGE_KEY = 'khothi-c360-v1';

const pad = (n: number) => String(n).padStart(6, '0');

export interface SignInput {
  kind: SignKind;
  title?: string;
  customerKey?: string;
  posCustomerId?: string;
  cardId?: string;
  usageId?: string;
  visitDate?: string;
  coveredKeys?: string[];
  sections: DocSection[];
  signerName: string;
  signerPhone: string;
  witness: string;
  branchId: string;
  /** PNG data URL */
  signature: string;
  /** Ghi lùi thời điểm ký (dữ liệu demo / nhập bù) */
  signedAt?: string;
  demo?: boolean;
}

export interface PhotoInput extends Omit<TreatmentPhoto, 'id' | 'createdAt' | 'mediaId' | 'thumbId'> {
  dataUrl: string;
  thumbUrl: string;
}

interface C360State {
  config: Customer360Config;
  /** Mã khách CMS → mã khách POS liên kết tay */
  links: Record<string, string[]>;
  overrides: Record<string, ClassOverride | null>;
  classLogs: Record<string, ClassLog[]>;
  photos: TreatmentPhoto[];
  docs: SignDoc[];
  visitRecords: VisitRecord[];
  seq: { photo: number; doc: number };
  /** Nhật ký sửa hồ sơ theo mã khách CMS (API chưa trả giá trị trước/sau nên ghi tại máy) */
  auditLogs: Record<string, AuditLog[]>;
  /** Khách đã nạp dữ liệu demo: hồ sơ POS đã liên kết + mốc áp dụng ký trước khi nạp */
  demo: Record<string, DemoMeta>;

  setConfig: (patch: Partial<Customer360Config>, by: string) => void;
  link: (customerKey: string, posId: string, by: string) => void;
  unlink: (customerKey: string, posId: string, by: string) => void;
  setOverride: (customerKey: string, o: ClassOverride | null, by: string) => void;
  log: (customerKey: string, by: string, text: string) => void;
  addPhotos: (items: PhotoInput[]) => Promise<TreatmentPhoto[]>;
  updatePhoto: (id: string, patch: Partial<Pick<TreatmentPhoto, 'phase' | 'angle' | 'note' | 'cardId' | 'sessionNo' | 'takenAt'>>) => void;
  hidePhoto: (id: string, by: string, reason: string) => void;
  restorePhoto: (id: string) => void;
  /** Xóa hẳn ảnh chụp nhầm (chỉ quản lý) */
  removePhoto: (id: string) => Promise<void>;
  sign: (input: SignInput) => Promise<SignDoc>;
  revokeDoc: (id: string, by: string, reason: string) => void;
  markPrinted: (id: string) => void;
  audit: (customerKey: string, entry: Omit<AuditLog, 'id' | 'at'> & { at?: string }) => void;
  /** Ghi đánh giá / ký giấy / hẹn ký sau cho một lần đến */
  upsertVisitRecord: (id: string, patch: Partial<Omit<VisitRecord, 'id'>> & Pick<VisitRecord, 'posCustomerId' | 'date'>) => void;
}

const now = () => new Date().toISOString();

const useC360Store = create<C360State>()(
  persist(
    (set, get) => ({
      // Bắt đầu đòi ký xác nhận lần đến từ ngày cài đặt — dữ liệu cũ không bị báo thiếu chữ ký
      config: { ...DEFAULT_C360_CONFIG, visitSignSince: new Date().toISOString().slice(0, 10) },
      links: {},
      overrides: {},
      classLogs: {},
      photos: [],
      docs: [],
      visitRecords: [],
      seq: { photo: 0, doc: 0 },
      demo: {},
      auditLogs: {},

      setConfig: (patch, by) => set(s => ({ config: { ...s.config, ...patch }, classLogs: { ...s.classLogs, _config: [{ at: now(), by, text: 'Cập nhật quy tắc phân loại' }, ...(s.classLogs._config || [])] } })),

      link: (key, posId, by) => {
        set(s => ({ links: { ...s.links, [key]: Array.from(new Set([...(s.links[key] || []), posId])) } }));
        get().log(key, by, `Liên kết hồ sơ bán hàng ${posId}`);
        get().audit(key, { by, kind: 'link', title: `Liên kết hồ sơ bán hàng ${posId}` });
      },
      unlink: (key, posId, by) => {
        set(s => ({ links: { ...s.links, [key]: (s.links[key] || []).filter(x => x !== posId) } }));
        get().log(key, by, `Bỏ liên kết hồ sơ bán hàng ${posId}`);
        get().audit(key, { by, kind: 'link', title: `Bỏ liên kết hồ sơ bán hàng ${posId}` });
      },

      setOverride: (key, o, by) => {
        set(s => ({ overrides: { ...s.overrides, [key]: o } }));
        const text = o
          ? `Phân loại tay: ${[o.tier && `hạng ${o.tier}`, o.segment && `nhóm "${SEGMENT[o.segment].label}"`].filter(Boolean).join(', ')}${o.until ? ` đến ${o.until.split('-').reverse().join('/')}` : ''} – ${o.reason}`
          : 'Gỡ phân loại tay, quay về phân loại tự động';
        get().log(key, by, text);
        get().audit(key, { by, kind: 'class', title: o ? 'Phân loại tay' : 'Gỡ phân loại tay', note: text });
      },
      log: (key, by, text) => set(s => ({ classLogs: { ...s.classLogs, [key]: [{ at: now(), by, text }, ...(s.classLogs[key] || [])].slice(0, 200) } })),

      addPhotos: async items => {
        let n = get().seq.photo;
        const created: TreatmentPhoto[] = [];
        for (const { dataUrl, thumbUrl, ...meta } of items) {
          n += 1;
          const id = `ANH-${pad(n)}`;
          await putMedia(`${id}:full`, dataUrl);
          await putMedia(`${id}:thumb`, thumbUrl);
          created.push({ ...meta, id, createdAt: now(), mediaId: `${id}:full`, thumbId: `${id}:thumb` });
        }
        set(s => ({ photos: [...created, ...s.photos], seq: { ...s.seq, photo: n } }));
        return created;
      },
      updatePhoto: (id, patch) => set(s => ({ photos: s.photos.map(p => (p.id === id ? { ...p, ...patch } : p)) })),
      hidePhoto: (id, by, reason) => set(s => ({ photos: s.photos.map(p => (p.id === id ? { ...p, hidden: { at: now(), by, reason } } : p)) })),
      restorePhoto: id => set(s => ({ photos: s.photos.map(p => (p.id === id ? { ...p, hidden: undefined } : p)) })),
      removePhoto: async id => {
        const p = get().photos.find(x => x.id === id);
        if (!p) return;
        await delMedia(p.mediaId);
        await delMedia(p.thumbId);
        set(s => ({ photos: s.photos.filter(x => x.id !== id) }));
      },

      sign: async input => {
        const n = get().seq.doc + 1;
        const id = `KY-${pad(n)}`;
        const signedAt = input.signedAt || now();
        const { signature, ...rest } = input;
        await putMedia(`${id}:sig`, signature);
        const hash = await digest(JSON.stringify({ sections: input.sections, signerName: input.signerName, signerPhone: input.signerPhone, signedAt }) + signature);
        const doc: SignDoc = {
          ...rest, id, title: input.title || SIGN_KIND[input.kind].label, signatureId: `${id}:sig`, hash, signedAt,
          device: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 160) : '', status: 'signed', printCount: 0,
        };
        set(s => ({ docs: [doc, ...s.docs], seq: { ...s.seq, doc: n } }));
        return doc;
      },
      revokeDoc: (id, by, reason) => set(s => ({ docs: s.docs.map(d => (d.id === id ? { ...d, status: 'revoked', revoked: { at: now(), by, reason } } : d)) })),
      markPrinted: id => set(s => ({ docs: s.docs.map(d => (d.id === id ? { ...d, printCount: d.printCount + 1 } : d)) })),
      audit: (key, entry) => set(s => ({
        auditLogs: { ...s.auditLogs, [key]: [{ ...entry, id: `NK-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, at: entry.at || now() }, ...(s.auditLogs[key] || [])].slice(0, 500) },
      })),
      upsertVisitRecord: (id, patch) => set(s => {
        const cur = s.visitRecords.find(r => r.id === id);
        const next: VisitRecord = { ...(cur || { id }), ...patch, id };
        return { visitRecords: cur ? s.visitRecords.map(r => (r.id === id ? next : r)) : [next, ...s.visitRecords] };
      }),
    }),
    {
      name: C360_STORAGE_KEY,
      version: 1,
      merge: (persisted, current) => {
        const p = (persisted || {}) as Partial<C360State>;
        return { ...current, ...p, config: { ...current.config, ...(p.config || {}) } };
      },
    },
  ),
);

// Đồng bộ giữa các tab (VD máy tính bảng ký ở quầy, CMS mở ở tab khác)
if (typeof window !== 'undefined') {
  window.addEventListener('storage', e => {
    if (e.key === C360_STORAGE_KEY) useC360Store.persist.rehydrate();
  });
}

export default useC360Store;
